// ============================================================================
// /bids/settings — where Bryan confirms the rates. The sibling of the dialysis
// stock settings: a change is a NEW row with a reason, the old one stays on the
// record, and every row shows the sheet and tab it came from. Comp exclusions
// are rows here too — add or clear, never delete. Everyone reads; only an
// executive writes (bid_set_setting refuses anyone else).
// ============================================================================

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, ErrorState, PageHeader, Skeleton } from '../../shared/components';
import { SmartSelect } from '../../components/smart-select';
import { StockWindow } from '../supply-management/dialysis-stock/stock-window';
import { fetchFacilityNames, fetchIsExecutive, fetchMarkets, fetchPeopleNames, fetchSettings, setSetting, FACILITY_TYPES, typeLabel, type Option, type SettingRow } from './api';
import { Field, INPUT } from './fields';
import { shortDate, parseNumber } from './format';
import { SETTING_WORDS, showValue, toStored, toTyped } from './settings-labels';
import { useBidLoad } from './use-bid-load';
import { useFacilitiesCleaning } from '../account-management/hooks/use-facility-cleaning';
import { nameWithCleaner } from './comp-names';

type Scope = 'company' | 'facility_type' | 'market' | 'facility';
const GROUPS: { key: string; title: string; match: (r: SettingRow) => boolean }[] = [
  { key: 'sc', title: 'Surgery centers', match: (r) => r.scope === 'facility_type' && r.facility_type === 'Surgery_Center' },
  { key: 'dia', title: 'Dialysis clinics', match: (r) => r.scope === 'facility_type' && r.facility_type === 'Dialysis' },
  { key: 'other', title: 'Other kinds of building', match: (r) => r.scope === 'facility_type' && !['Surgery_Center', 'Dialysis'].includes(r.facility_type ?? '') },
  { key: 'company', title: 'Every bid', match: (r) => r.scope === 'company' },
  { key: 'market', title: 'By market', match: (r) => r.scope === 'market' },
  { key: 'facility', title: 'Buildings left out of the comparables', match: (r) => r.scope === 'facility' },
];

function groupKey(r: SettingRow): string {
  return [r.setting_key, r.scope, r.facility_type, r.market_region, r.state, r.facility_id].join('|');
}
// A building is always named with its cleaner (set by the page before render).
let FACILITY_LABELS = new Map<string, string>();
function whereText(r: SettingRow): string {
  if (r.scope === 'company') return 'every bid';
  if (r.scope === 'market') return r.market_region ?? '';
  if (r.scope === 'facility') return FACILITY_LABELS.get(r.facility_id ?? '') ?? r.facility_id ?? '';
  return [typeLabel(r.facility_type), r.market_region, r.state].filter(Boolean).join(' · ');
}

interface Target { key: string; scope: Scope; facilityType: string | null; market: string | null; state: string | null; facilityId: string | null; current: SettingRow | null }

function EditWindow({ target, onClose, markets }: { target: Target | null; onClose: () => void; markets: Option[] }) {
  const [key, setKey] = useState(target?.key ?? '');
  const [scope, setScope] = useState<Scope>(target?.scope ?? 'facility_type');
  const [ft, setFt] = useState(target?.facilityType ?? '');
  const [market, setMarket] = useState(target?.market ?? '');
  const [state, setState] = useState(target?.state ?? '');
  const [facility, setFacility] = useState(target?.facilityId ?? '');
  const [value, setValue] = useState(target?.current ? toTyped(target.key, target.current.value) : '');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  if (!target) return null;
  const isNew = !target.key;
  const isExclusion = key === 'exclude_from_comps';
  const n = isExclusion ? 1 : parseNumber(value);
  const canSave = !!key && reason.trim().length > 0 && n != null
    && (scope !== 'facility_type' || !!ft) && (scope !== 'market' || !!market) && (scope !== 'facility' || !!facility);
  const save = async () => {
    await setSetting({ scope: isExclusion ? 'facility' : scope, key, value: isExclusion ? 1 : toStored(key, n as number),
      facilityType: scope === 'facility_type' ? ft : null, marketRegion: scope === 'facility_type' || scope === 'market' ? market || null : null,
      state: scope === 'facility_type' ? state || null : null, facilityId: isExclusion ? facility : null, until: until || null, reason: reason.trim() });
    onClose();
  };
  const clear = target.current ? async () => {
    if (!reason.trim()) throw new Error('Say why it is being cleared — the reason stays on the record.');
    await setSetting({ scope: target.scope, key: target.key, value: null, facilityType: target.facilityType, marketRegion: target.market,
      state: target.state, facilityId: target.facilityId, reason: reason.trim(), clear: true });
    onClose();
  } : undefined;
  return (
    <StockWindow open title={isNew ? (isExclusion ? 'Leave a building out of the comparables' : 'Add a rate') : SETTING_WORDS[key]?.label ?? key}
      subtitle={isNew ? undefined : whereText(target.current as SettingRow)} canSave={canSave} onCancel={onClose} onSave={save}
      secondary={clear ? { label: target.key === 'exclude_from_comps' ? 'Put it back in' : 'Clear at this level', onClick: clear } : undefined}>
      {isNew && !isExclusion && (
        <>
          <Field label="Rate">
            <select className={INPUT} value={key} onChange={(e) => setKey(e.target.value)}>
              <option value="">— pick —</option>
              {Object.entries(SETTING_WORDS).filter(([k]) => k !== 'exclude_from_comps').map(([k, w]) => <option key={k} value={k}>{w.label}</option>)}
            </select>
          </Field>
          <Field label="Applies to">
            <select className={INPUT} value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
              <option value="facility_type">One kind of building</option>
              <option value="market">One market</option>
              <option value="company">Every bid</option>
            </select>
          </Field>
          {scope === 'facility_type' && (
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Kind of building"><select className={INPUT} value={ft} onChange={(e) => setFt(e.target.value)}><option value="">— pick —</option>{FACILITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></Field>
              <Field label="Only in market (optional)"><select className={INPUT} value={market} onChange={(e) => { setMarket(e.target.value); if (e.target.value) setState(''); }}><option value="">—</option>{markets.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select></Field>
              <Field label="Only in state (optional)"><input className={INPUT} maxLength={2} value={state} onChange={(e) => { setState(e.target.value.toUpperCase()); if (e.target.value) setMarket(''); }} /></Field>
            </div>
          )}
          {scope === 'market' && (
            <Field label="Market"><select className={INPUT} value={market} onChange={(e) => setMarket(e.target.value)}><option value="">— pick —</option>{markets.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select></Field>
          )}
        </>
      )}
      {isNew && isExclusion && (
        <Field label="Building">
          <SmartSelect entity="facility" value={facility || null} onChange={(id) => setFacility(id)} placeholder="Search facilities…" />
        </Field>
      )}
      {!isExclusion && (
        <Field label={SETTING_WORDS[key]?.unit === 'pct' ? 'Value (%)' : 'Value'} hint={target.current ? `Now ${showValue(target.key, target.current.value)}` : undefined}>
          <input className={INPUT} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      )}
      {!isExclusion && (
        <Field label="Keep until (optional)"><input className={INPUT} type="date" value={until} onChange={(e) => setUntil(e.target.value)} /></Field>
      )}
      <Field label="Reason (required)"><input className={INPUT} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </StockWindow>
  );
}

export default function PricingSettings() {
  const { data, loading, error, refetch } = useBidLoad<{ rows: SettingRow[]; exec: boolean; markets: Option[]; names: Map<string, string>; people: Map<string, string> }>(
    async () => {
      const [rows, exec, markets] = await Promise.all([fetchSettings(), fetchIsExecutive(), fetchMarkets()]);
      const fids = [...new Set(rows.map((r) => r.facility_id).filter((x): x is string => !!x))];
      const eids = [...new Set(rows.map((r) => r.set_by_eid).filter((x): x is string => !!x))];
      const [names, people] = await Promise.all([fetchFacilityNames(fids), fetchPeopleNames(eids)]);
      return { rows, exec, markets, names, people };
    },
    [],
    { rows: [], exec: false, markets: [], names: new Map(), people: new Map() },
  );
  const facilityIds = useMemo(() => [...data.names.keys()], [data.names]);
  const { cleaning } = useFacilitiesCleaning(facilityIds);
  FACILITY_LABELS = new Map([...data.names].map(([id, n]) => [id, nameWithCleaner(n, cleaning.get(id))]));
  PEOPLE = data.people;
  const [target, setTarget] = useState<Target | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  // Newest row per setting per scope is the one in force at that scope; older rows are its history.
  const grouped = useMemo(() => {
    const m = new Map<string, SettingRow[]>();
    for (const r of data.rows) {
      const k = groupKey(r);
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.values()];
  }, [data.rows]);

  if (loading) return <div className="p-6"><Skeleton className="h-64 w-full" /></div>;
  if (error) return <div className="p-6"><ErrorState body={error} onRetry={refetch} /></div>;

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader
        eyebrow="Bids"
        title="Pricing settings"
        meta="Seeded from the September prep sheets — every row says which sheet and tab. Bryan confirms them once; a change is a new row with a reason, and the old one stays on the record."
        actions={
          <>
            <Button size="sm" variant="secondary" as={Link} to="/bids">Back to Bids</Button>
            {data.exec && <Button size="sm" variant="secondary" onClick={() => setTarget({ key: 'exclude_from_comps', scope: 'facility', facilityType: null, market: null, state: null, facilityId: null, current: null })}>Leave a building out</Button>}
            {data.exec && <Button size="sm" onClick={() => setTarget({ key: '', scope: 'facility_type', facilityType: null, market: null, state: null, facilityId: null, current: null })}>Add a rate</Button>}
          </>
        }
      />
      {!data.exec && <p className="text-sm text-tcs-text-muted">Only an executive can change a rate. Everyone can read them.</p>}
      {GROUPS.map((g) => {
        const list = grouped.filter((rows) => g.match(rows[0]));
        if (!list.length) return null;
        return (
          <section key={g.key} className="rounded-xl border border-tcs-border bg-tcs-bg">
            <h2 className="border-b border-tcs-border px-4 py-3 text-base font-semibold text-tcs-text">{g.title}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-tcs-text-muted">
                  <tr className="border-b border-tcs-border"><th className="px-3 py-2">Rate</th><th className="px-3 py-2">Applies to</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Set</th><th className="px-3 py-2" /></tr>
                </thead>
                <tbody>
                  {list.map((rows) => {
                    const cur = rows[0];
                    const k = groupKey(cur);
                    return (
                      <FragmentRows key={k} rows={rows} open={!!open[k]} onToggle={() => setOpen((o) => ({ ...o, [k]: !o[k] }))}
                        onEdit={data.exec ? () => setTarget({ key: cur.setting_key, scope: cur.scope as Scope, facilityType: cur.facility_type, market: cur.market_region, state: cur.state, facilityId: cur.facility_id, current: cur }) : undefined} />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
      {target && <EditWindow key={`${target.key}|${target.scope}|${target.facilityId}|${target.facilityType}|${target.market}|${target.state}`} target={target} markets={data.markets} onClose={() => setTarget(null)} />}
    </div>
  );
}

let PEOPLE = new Map<string, string>();
function who(eid: string | null): string {
  return eid ? ` · ${PEOPLE.get(eid) ?? eid}` : ' · seeded';
}

function FragmentRows({ rows, open, onToggle, onEdit }: { rows: SettingRow[]; open: boolean; onToggle: () => void; onEdit?: () => void }) {
  const cur = rows[0];
  const expired = cur.effective_until != null && cur.effective_until < new Date().toISOString().slice(0, 10);
  return (
    <>
      <tr className="border-b border-tcs-border align-top">
        <td className="px-3 py-2 font-medium text-tcs-text">{SETTING_WORDS[cur.setting_key]?.label ?? cur.setting_key}
          {cur.reason && <div className="text-xs font-normal text-tcs-text-muted">{cur.reason}</div>}
        </td>
        <td className="px-3 py-2">{whereText(cur)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{cur.is_cleared ? <span className="text-tcs-text-muted">cleared</span> : showValue(cur.setting_key, cur.value)}{expired ? <div className="text-xs text-st-warn-fg">ended {cur.effective_until}</div> : null}</td>
        <td className="px-3 py-2 text-xs text-tcs-text-secondary">{cur.source ?? '—'}</td>
        <td className="px-3 py-2 text-xs text-tcs-text-muted">{shortDate(cur.set_at)}{who(cur.set_by_eid)}</td>
        <td className="px-3 py-2 text-right">
          <div className="flex justify-end gap-2">
            {rows.length > 1 && <button type="button" className="text-xs underline" onClick={onToggle}>{open ? 'Hide history' : `History (${rows.length - 1})`}</button>}
            {onEdit && <button type="button" className="text-xs underline" onClick={onEdit}>Change</button>}
          </div>
        </td>
      </tr>
      {open && rows.slice(1).map((r) => (
        <tr key={r.setting_id} className="border-b border-tcs-border bg-tcs-surface text-xs text-tcs-text-muted">
          <td className="px-3 py-1.5 pl-6">earlier</td><td className="px-3 py-1.5" />
          <td className="px-3 py-1.5 text-right">{r.is_cleared ? 'cleared' : showValue(r.setting_key, r.value)}</td>
          <td className="px-3 py-1.5">{r.reason ?? r.source ?? ''}</td><td className="px-3 py-1.5">{shortDate(r.set_at)}{who(r.set_by_eid)}</td><td />
        </tr>
      ))}
    </>
  );
}
